import { getToken } from '_services/user-service';

const errorString = (response) => {
    return (
        "Error " + response.status + ": " + response.statusText
    )
}

// fetch the staff list for the caller's organisation
export const getEmployeeList = async ({ limit = 25, skip = 0, search = '' } = {}) => {
    const token = await getToken();
    const params = new URLSearchParams({
        limit: String(limit),
        skip: String(skip),
    });
    if (search) {
        params.set('search', search);
    }
    const url = `/api/v1/mobileusers/portalusers/staff/employees?${params.toString()}`;

    return new Promise(async (resolve, reject) => {
        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    accept: "application/json",
                    "Content-Type": "application/json",
                    "Firebase-UID": token,
                }
            });

            let json = await response.json();

            if (response.status >= 400 && response.status < 600) {
                throw new Error(errorString(response));
            }

            resolve(json);
        } catch (e) {
            reject(new Error("Error getting employee list"));
        }
    });
};

// invite a staff member into the caller's organisation
export const inviteEmployee = async (inviteData) => {
    const token = await getToken();
    const url = "/api/v1/mobileusers/portalusers/staff/employees/invite";

    return new Promise(async (resolve, reject) => {
        try {
            const response = await fetch(url, {
                method: 'POST',
                body: JSON.stringify(inviteData),
                headers: {
                    accept: "application/json",
                    "Content-Type": "application/json",
                    "Firebase-UID": token,
                }
            });

            let json = await response.json();

            if (response.status >= 400 && response.status < 600) {
                throw new Error(json?.errors?.[0]?.msg || errorString(response));
            }

            resolve(json);
        } catch (e) {
            reject(new Error(e.message || "Error inviting employee"));
        }
    });
};

// update a staff member's role
export const updateEmployeeRole = async (employeeId, role) => {
    const token = await getToken();
    const url = `/api/v1/mobileusers/portalusers/staff/employees/${employeeId}/role`;

    return new Promise(async (resolve, reject) => {
        try {
            const response = await fetch(url, {
                method: 'PUT',
                body: JSON.stringify({ role }),
                headers: {
                    accept: "application/json",
                    "Content-Type": "application/json",
                    "Firebase-UID": token,
                }
            });

            let json = await response.json();

            if (response.status >= 400 && response.status < 600) {
                throw new Error(json?.errors?.[0]?.msg || errorString(response));
            }

            resolve({ id: employeeId, role: role });
        } catch (e) {
            reject(new Error(e.message || "Error updating employee role"));
        }
    });
};

// remove a staff member from the portal
export const deleteEmployee = async (employeeId) => {
    const token = await getToken();
    const url = `/api/v1/mobileusers/portalusers/staff/employees/${employeeId}`;

    return new Promise(async (resolve, reject) => {
        try {
            const response = await fetch(url, {
                method: 'DELETE',
                headers: {
                    accept: "application/json",
                    "Firebase-UID": token,
                }
            });

            if (response.status >= 400 && response.status < 600) {
                throw new Error(errorString(response));
            }

            resolve(employeeId);
        } catch (e) {
            reject(new Error("Error deleting employee"));
        }
    });
};

// resend an invitation (fresh temp password + email)
export const resendInvitation = async (employeeId) => {
    const token = await getToken();
    const url = `/api/v1/mobileusers/portalusers/staff/employees/${employeeId}/resend-invitation`;

    return new Promise(async (resolve, reject) => {
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    accept: "application/json",
                    "Content-Type": "application/json",
                    "Firebase-UID": token,
                }
            });

            let json = await response.json();

            if (response.status >= 400 && response.status < 600) {
                throw new Error(json?.errors?.[0]?.msg || errorString(response));
            }

            resolve(json);
        } catch (e) {
            reject(new Error(e.message || "Error resending invitation"));
        }
    });
};

// require password reset + MFA for an existing staff member (re-syncs claims,
// sets tempPassword + mfaRequired, rotates password and emails a temp one)
export const requireEmployeeMfa = async (employeeId) => {
    const token = await getToken();
    const url = `/api/v1/mobileusers/portalusers/staff/employees/${employeeId}/require-mfa`;

    return new Promise(async (resolve, reject) => {
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    accept: "application/json",
                    "Content-Type": "application/json",
                    "Firebase-UID": token,
                }
            });

            let json = await response.json();

            if (response.status >= 400 && response.status < 600) {
                throw new Error(json?.errors?.[0]?.msg || errorString(response));
            }

            resolve(json);
        } catch (e) {
            reject(new Error(e.message || "Error requiring MFA"));
        }
    });
};

// send a password reset email to a staff member
export const sendPasswordReset = async (email) => {
    const token = await getToken();
    const url = `/api/v1/mobileusers/portalusers/reset-password?email=${encodeURIComponent(email)}`;

    return new Promise(async (resolve, reject) => {
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    accept: "application/json",
                    "Content-Type": "application/json",
                    "Firebase-UID": token,
                }
            });

            let json = await response.json();

            if (response.status >= 400 && response.status < 600) {
                throw new Error(json?.errors?.[0]?.msg || errorString(response));
            }

            resolve(json);
        } catch (e) {
            reject(new Error(e.message || "Error sending password reset email"));
        }
    });
};

// create a new organisation admin (super admin only)
export const createAdminAccount = async (adminData) => {
    const token = await getToken();
    const url = "/api/v1/mobileusers/portalusers/newAdmin";

    return new Promise(async (resolve, reject) => {
        try {
            const response = await fetch(url, {
                method: 'POST',
                body: JSON.stringify(adminData),
                headers: {
                    accept: "application/json",
                    "Content-Type": "application/json",
                    "Firebase-UID": token,
                }
            });

            let json = await response.json();

            if (response.status >= 400 && response.status < 600) {
                throw new Error(json?.errors?.[0]?.msg || errorString(response));
            }

            resolve(json);
        } catch (e) {
            reject(new Error(e.message || "Error creating admin account"));
        }
    });
};

// confirm the temporary password was changed (clears the tempPassword claim)
export const confirmPasswordChanged = async () => {
    // the change just revoked the cached token
    const token = await getToken(true);
    const url = "/api/v1/mobileusers/portalusers/confirm-password-changed";

    return new Promise(async (resolve, reject) => {
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    accept: "application/json",
                    "Content-Type": "application/json",
                    "Firebase-UID": token,
                }
            });

            const json = await response.json();

            if (response.status >= 400 && response.status < 600) {
                throw new Error(errorString(response));
            }

            resolve(json);
        } catch (e) {
            reject(new Error("Error confirming password change"));
        }
    });
};

// confirm MFA setup completed (server verifies enrollment before updating claims)
export const confirmMfaCompleted = async () => {
    // the change just revoked the cached token
    const token = await getToken(true);
    const url = "/api/v1/mobileusers/portalusers/confirm-mfa-completed";

    return new Promise(async (resolve, reject) => {
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    accept: "application/json",
                    "Content-Type": "application/json",
                    "Firebase-UID": token,
                }
            });

            const json = await response.json();

            if (response.status >= 400 && response.status < 600) {
                throw new Error(errorString(response));
            }

            resolve(json);
        } catch (e) {
            reject(new Error("Error confirming MFA completion"));
        }
    });
};
